import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Table as ReactTable,
  useReactTable,
} from '@tanstack/react-table';
import { createContext, useContext } from 'react';

import { cn } from '@/lib/cn';

import { Table } from '../table';
import type { DataTableProps } from './types';

// -- Context: table 인스턴스를 서브컴포넌트에서 접근 --
const DataTableContext = createContext<ReactTable<any> | null>(null);

export function useDataTableContext<TData = any>(): ReactTable<TData> {
  const ctx = useContext(DataTableContext);
  if (!ctx) {
    throw new Error('useDataTableContext must be used within a DataTable');
  }
  return ctx as ReactTable<TData>;
}

// -- 로딩 skeleton 행 --
const SkeletonRows = ({ colCount }: { colCount: number }) => (
  <>
    {Array.from({ length: 5 }).map((_, rowIdx) => (
      <Table.Row key={rowIdx}>
        {Array.from({ length: colCount }).map((_, colIdx) => (
          <Table.Cell key={colIdx} className="flex-1">
            <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
          </Table.Cell>
        ))}
      </Table.Row>
    ))}
  </>
);

// -- 메인 DataTable 컴포넌트 --
const DataTable = <TData extends unknown>({
  columns,
  data,
  loading = false,
  sorting,
  onChangeSorting,
  enableRowSelection,
  rowSelection,
  onChangeRowSelection,
  getRowId,
  onClickRow,
  toolbar,
  emptyState,
  pagination,
  className,
  headerClassName,
  rowClassName,
  size,
}: DataTableProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    state: {
      ...(sorting !== undefined && { sorting }),
      ...(rowSelection !== undefined && { rowSelection }),
    },
    onSortingChange: onChangeSorting,
    onRowSelectionChange: onChangeRowSelection,
    enableRowSelection,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const isEmpty = rows.length === 0 && !loading;

  return (
    <DataTableContext.Provider value={table}>
      {toolbar}
      <Table.Root className={className}>
        {/* 헤더 */}
        {headerGroups.map((headerGroup) => (
          <Table.Header key={headerGroup.id} className={headerClassName}>
            {headerGroup.headers.map((header) => (
              <Table.Col
                key={header.id}
                style={{ width: header.getSize() }}
                className={header.column.columnDef.meta?.className}
                size={size}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </Table.Col>
            ))}
          </Table.Header>
        ))}

        {/* 로딩 */}
        {loading && <SkeletonRows colCount={columns.length} />}

        {/* 빈 상태 */}
        {isEmpty && emptyState}

        {/* 데이터 행 */}
        {!loading &&
          rows.map((row) => {
            const resolvedRowClassName =
              typeof rowClassName === 'function'
                ? rowClassName(row)
                : rowClassName;

            return (
              <Table.Row
                key={row.id}
                className={cn(
                  row.getIsSelected() && 'bg-gray-100',
                  resolvedRowClassName,
                )}
                onClick={
                  onClickRow ? () => onClickRow(row.original) : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className={cell.column.columnDef.meta?.className}
                    size={size}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
      </Table.Root>

      {/* 페이지네이션 슬롯 */}
      {pagination}
    </DataTableContext.Provider>
  );
};

export default DataTable;
export { DataTableContext };
